import { normalizePhone } from '@btsis/core';
import { z } from 'zod';

/**
 * The reason a telephone is refused, carried as the Zod issue's message so the
 * API can answer it as its own code: `apps/api/src/common/errors.ts` turns an
 * input-validation failure whose issue says this into 422 `VALIDATION_FAILED`
 * with `details.reason` `PHONE_INVALID` and the field's path, which the screens
 * render in Turkish on the field (docs/decisions/0086).
 */
export const PHONE_INVALID = 'PHONE_INVALID';

/**
 * Every telephone the API is sent: a valid number in **international form**,
 * answered as E.164. The country is chosen on the screen, by the phone field's
 * selector, so a national number never reaches the API — it would need a
 * country the server would have to guess (docs/decisions/0086). Spaces and
 * separators are tolerated, since they carry nothing; the handler always
 * receives `+905321112233`. Validated by `normalizePhone`, the same function
 * every screen and every write uses.
 */
export const phoneSchema = z
  .string()
  .trim()
  .max(32)
  .transform((value, context) => e164OrIssue(value, context));

/**
 * The same, where a blank value is how a person clears the number — a user's
 * own telephone, set and cleared on the user screen. Blank answers `''`.
 */
export const clearablePhoneSchema = z
  .string()
  .trim()
  .max(32)
  .transform((value, context) => (value === '' ? '' : e164OrIssue(value, context)));

function e164OrIssue(value: string, context: z.RefinementCtx): string {
  const e164 = normalizePhone(value);
  if (e164 === null) {
    context.addIssue({ code: 'custom', message: PHONE_INVALID });
    return z.NEVER;
  }
  return e164;
}
